import { useState, useEffect } from "react";
import { createContainer } from "unstated-next";

import { Env } from "../../enums/env.enum";
import { Logo } from "../../enums/logo.enum";
import { Theme } from "../../enums/theme.enum";
import {
    getApp,
    getAppLogo,
    getAppColor,
    getHeaderLogo,
    lightTheme as currentLightTheme,
    darkTheme as currentDarkTheme,
    isDevMode,
    isTrialMode
} from "../../utilities/routes/routes.util";
import { stringGetter } from "../../utilities/localstorage-dealer/localstorage-getters.util";
import { setDataToLocalStorage } from "../../utilities/localstorage-dealer/localstorage-setter.util";
import { isMobileDevice } from "../../utilities/platform/platform.util";

function useTheme() {
    const CURRENT_THEME = "CURRENT_THEME";
    const CURRENT_DEV_ENV = "CURRENT_DEV_ENV";
    const LEFT_MENU_COLLAPSED = "LEFT_MENU_COLLAPSED";
    const [devEnv, setDevEnv] = useState<Env>();
    const [devMode] = useState<boolean>(isDevMode);
    const [isTrial, setIsTrial] = useState<boolean>(isTrialMode);
    const [color, setColor] = useState<string>(getAppColor());
    const [logo, setLogo] = useState<Logo>(getAppLogo());
    const [headerLogo, setHeaderLogo] = useState<Logo>(getHeaderLogo());
    const [lightTheme, setLightTheme] = useState<Theme>(currentLightTheme);
    const [darkTheme, setDarkTheme] = useState<Theme>(currentDarkTheme);
    const [theme, setTheme] = useState<Theme>((stringGetter(CURRENT_THEME) as Theme) ?? lightTheme);
    const [isLightTheme, setIsLightTheme] = useState<boolean>(theme === lightTheme);
    const [leftMenuCollapsed, setLeftMenuCollapsed] = useState<boolean>(
        stringGetter(LEFT_MENU_COLLAPSED) === "true" && !isMobileDevice()
    );
    const [leftMenuShown, setLeftMenuShown] = useState<boolean>(false);
    const [isLoadingLoader, setLoadingLoader] = useState<boolean>(false);

    useEffect(() => {
        setIsLightTheme(theme === lightTheme);
    }, [theme, lightTheme]);

    useEffect(() => {
        if (devMode) {
            const currentDevEnv = stringGetter(CURRENT_DEV_ENV);
            setDevEnv(
                Object.values(Env).includes(currentDevEnv as Env)
                    ? (currentDevEnv as Env)
                    : Env.trial
            );
        }
    }, [devMode]);

    useEffect(() => {
        if (devEnv) {
            const devApp = getApp(devEnv);

            if (devApp) {
                setLightTheme(devApp.lightTheme);
                setDarkTheme(devApp.darkTheme);
                setTheme(currentTheme => {
                    const newTheme = currentTheme.includes("light")
                        ? devApp.lightTheme
                        : devApp.darkTheme;
                    setDataToLocalStorage(CURRENT_THEME, newTheme);
                    return newTheme;
                });
                setColor(devApp.color);
                setLogo(devApp.logo);
                setHeaderLogo(devApp.headerLogo);
                setIsTrial(
                    devApp.subdomains.includes(Env.trial) ||
                        devApp.subdomains.includes(`admin.${Env.trial}`)
                );
            }
        }
    }, [devEnv]);

    const changeDevEnv = (env: Env) => {
        setDevEnv(env);
        setDataToLocalStorage(CURRENT_DEV_ENV, env);
    };

    const changeTheme = () => {
        const newTheme = isLightTheme ? darkTheme : lightTheme;
        setTheme(newTheme);
        setDataToLocalStorage(CURRENT_THEME, newTheme);
    };

    const collapseLeftMenu = (value: boolean) => {
        setLeftMenuCollapsed(value);
        setDataToLocalStorage(LEFT_MENU_COLLAPSED, value);
    };

    return {
        devMode,
        devEnv,
        isTrial,
        color,
        logo,
        headerLogo,
        isLightTheme,
        theme,
        leftMenuCollapsed,
        leftMenuShown,
        isLoadingLoader,
        changeDevEnv,
        changeTheme,
        collapseLeftMenu,
        setLeftMenuShown,
        setLoadingLoader
    };
}

export const ThemeContainer = createContainer(useTheme);
